import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction } from '@mui/material';
import './Dashboard.css';
import pricejewel from './priceimage/pricejewel.png';
import homeicon from './picture/homeicon.png';
import pricejewelbl from './priceimage/pricejewelbl.png';
import homeblicon from './picture/homeblicon.png';
import Sidebar from './SideBar';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { ariaHidden } from '@mui/material/Modal/ModalManager';

const Dashboard = ({ hideIntroduction }) => {
  const location = useLocation();
  const [selectedLink, setSelectedLink] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPriceUpdateButtonEnabled, setPriceUpdateButtonEnabled] = useState(false);
  const [isHomeButtonEnabled, setHomeButtonEnabled] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const sidebarRef = useRef(null);
  let collapseTimer = useRef(null);

  const roleReceived = localStorage.getItem('roleReceived');
  const isAdmin = roleReceived === 'Admin';
  const isUser = roleReceived === 'User';
  const isContentManager = roleReceived === 'ContentManager';

  useEffect(() => {
    const updateView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    updateView();
    window.addEventListener('resize', updateView);
    return () => {
      window.removeEventListener('resize', updateView);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === '/introduction') {
      setHomeButtonEnabled(true);
    } else {
      setHomeButtonEnabled(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/introduction') {
      setSelectedLink(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const templateRoutes = [
      '/priceupdates',
      ...(isAdmin || isContentManager ? ['/newtemplate', '/activatetemplate', '/managetemplate', '/managesms'] : []),
    ];
    if (templateRoutes.includes(location.pathname)) {
      setSelectedLink(location.pathname);
    } else if ((isAdmin || isUser) && (location.pathname === '/pan360' || location.pathname === '/aadhaar360')) {
      setSelectedLink(location.pathname);
    } else {
      setSelectedLink(null);
    }
  }, [location.pathname, isAdmin, isUser, isContentManager]);

  useEffect(() => {
    if (location.pathname === '/priceupdates') {
      setPriceUpdateButtonEnabled(true);
    } else {
      setPriceUpdateButtonEnabled(false);
    }
  }, [location.pathname]);

  const handleMouseEnter = () => {
    clearTimeout(collapseTimer.current);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    collapseTimer.current = setTimeout(() => {
      setIsExpanded(false);
      setActiveDropdown('');
    }, 1000);
  };

  const handleItemClick = (id) => {
    setActiveDropdown((prev) => (prev === id ? '' : id));
  };

  const handleLinkClick = (link) => {
    setSelectedLink(link);
    if (hideIntroduction) {
      hideIntroduction();
    }
  };

  const handleClickOutside = (e) => {
    if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
      setActiveDropdown('');
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const sidebarItems = [
    {
      label: 'Home',
      icon: 'fa-home',
      route: '/introduction',
      dropdown: false,
      access: ['Admin', 'User', 'ContentManager'],
    },
    {
      label: 'PAN',
      icon: 'fa-user-check',
      dropdown: true,
      id: 'pan',
      access: ['Admin', 'User'],
      items: [
        {
          label: 'PAN 360',
          route: '/pan360',
          access: ['Admin', 'User'],
        },
      ],
    },
    {
      label: 'Aadhaar',
      icon: 'fa-fingerprint',
      dropdown: true,
      id: 'aadhaar',
      access: ['Admin', 'User'],
      items: [
        {
          label: 'Aadhaar 360',
          route: '/aadhaar360',
          access: ['Admin', 'User'],
        },
      ],
    },
    {
      label: 'SMS Marketing',
      icon: 'fa-file-alt',
      dropdown: true,
      id: 'template',
      access: ['Admin', 'User', 'ContentManager'],
      items: [
        {
          label: 'Price Updates',
          route: '/priceupdates',
          access: ['Admin', 'User', 'ContentManager'],
        },
        ...(isAdmin || isContentManager
          ? [
              {
                label: 'New Template',
                route: '/newtemplate',
                access: ['Admin', 'ContentManager'],
              },
              {
                label: 'Manage Template',
                route: '/managetemplate',
                access: ['Admin', 'ContentManager'],
              },
              {
                label: 'Activate Template',
                route: '/activatetemplate',
                access: ['Admin', 'ContentManager'],
              },
              {
                label: 'Manage SMS',
                route: '/managesms',
                access: ['Admin'],
              },
            ]
          : []),
      ],
    },
    {
      label: 'WhatsApp Marketing',
      icon: 'fa-brands fa-whatsapp',
      ariaHidden: true,
      dropdown: true,
      id: 'whatsapp',
      access: ['ContentManager', 'Admin'],
      items: [
        {
          label: 'Templates',
          route: '/templates',
          access: ['ContentManager', 'Admin'],
        },
        {
          label: 'Campaign',
          route: '/campaign',
          access: ['ContentManager', 'Admin'],
        },
        {
          label: 'Contacts',
          route: '/contacts',
          access: ['ContentManager', 'Admin'],
        },
      ],
    },
  ];

  return (
    <div id="outerdashcont">
      <div
        className={`sidebar ${isExpanded ? 'expanded' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={sidebarRef}
      >
        {sidebarItems
          .filter((item) => item.access.includes(roleReceived))
          .map((item, index) => (
            <div key={index}>
              <div
                className={`sidebar-item ${
                  selectedLink === item.route || activeDropdown === item.id ? 'active' : ''
                }`}
                onClick={() => item.dropdown && handleItemClick(item.id)}
              >
                <i className={`fas ${item.icon}`}></i>
                <span>{item.label}</span>
                {item.dropdown && <i className="fas fa-chevron-right"></i>}
              </div>
              {item.dropdown && (
                <div
                  className={`sidebar-item-dropdown ${
                    activeDropdown === item.id ? 'active' : ''
                  }`}
                  id={item.id}
                >
                  {item.items
                    ?.filter((subItem) => subItem.access.includes(roleReceived))
                    .map((subItem, i) => (
                      <div
                        className={`dropdown-item ${
                          selectedLink === subItem.route ? 'active' : ''
                        }`}
                        key={i}
                      >
                        {subItem.disabled ? (
                          <span>
                            {subItem.label}
                          </span>
                        ) : (
                          <Link
                            to={subItem.route}
                            onClick={() => handleLinkClick(subItem.route)}
                          >
                            {subItem.label}
                          </Link>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
      </div>
      {isMobileView && (
        <BottomNavigation
          value={selectedLink}
          onChange={(event, newValue) => setSelectedLink(newValue)}
          className="bottom-navigation"
        >
          {sidebarItems
            .filter((item) => item.access.includes(roleReceived))
            .map((item, index) => (
              <BottomNavigationAction
                key={index}
                label={item.label}
                value={item.route || item.id}
                icon={<i className={`fas ${item.icon}`}></i>}
                component={item.route ? Link : undefined}
                to={item.route}
                onClick={
                  item.dropdown
                    ? () => handleItemClick(item.id)
                    : () => handleLinkClick(item.route)
                }
                sx={{
                  backgroundColor: selectedLink === (item.route || item.id) ? '#171717' : 'transparent',
                  '&.Mui-selected': {
                    backgroundColor: '#171717',
                    '& .MuiSvgIcon-root, & i': {
                      color: '#eed00e',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#171717',
                  },
                }}
                className={`bottom-navigation-selected ${
                  selectedLink === (item.route || item.id) ? 'active' : ''
                }`}
              />
            ))}
          {activeDropdown && (
            <div className={`${activeDropdown}-dropdown-mobile`}>
              <ul>
                {sidebarItems
                  .find((item) => item.id === activeDropdown)
                  ?.items?.filter((subItem) => subItem.access.includes(roleReceived))
                  .map((subItem, i) => (
                    <li key={i}>
                      {subItem.disabled ? (
                        <span>
                          {subItem.label}
                        </span>
                      ) : (
                        <Link
                          to={subItem.route}
                          onClick={() => handleLinkClick(subItem.route)}
                          style={{
                            backgroundColor: selectedLink === subItem.route ? '#eed00e' : '',
                            color: selectedLink === subItem.route ? '#000' : '#fff',
                          }}
                        >
                          {subItem.label}
                        </Link>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </BottomNavigation>
      )}
    </div>
  );
};

export default Dashboard;